import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import SEO from "./SEO";

const TEAL = "#4FB9AF";
const INK = "#0A0F1C";
const GOLDLINE = "#2E3A4E";

function Seal() {
  return (
    <div className="relative flex items-center justify-center" style={{ width: "26mm", height: "26mm" }}>
      <div
        className="absolute inset-0 rotate-45"
        style={{ border: `0.5mm solid ${TEAL}` }}
      />
      <div
        className="absolute rotate-45"
        style={{ inset: "2.5mm", border: `0.2mm solid ${GOLDLINE}` }}
      />
      <div className="text-center relative">
        <div className="font-display font-bold" style={{ fontSize: "13pt", color: TEAL }}>
          S
        </div>
        <div
          className="font-mono uppercase"
          style={{ fontSize: "4pt", letterSpacing: "0.25em", color: "#8A94A6" }}
        >
          Sebyx
        </div>
      </div>
    </div>
  );
}

function CertificateSheet({ cfg, name, date, city }) {
  return (
    <div
      data-testid={`certificate-${cfg.slug}`}
      className="relative mx-auto overflow-hidden"
      style={{
        width: "297mm",
        height: "210mm",
        background: INK,
        color: "#E8ECF3",
      }}
    >
      {/* frames */}
      <div className="absolute pointer-events-none" style={{ inset: "7mm", border: `0.6mm solid ${TEAL}` }} />
      <div className="absolute pointer-events-none" style={{ inset: "9.5mm", border: `0.2mm solid ${GOLDLINE}` }} />

      <div className="relative h-full flex flex-col items-center text-center" style={{ padding: "18mm 24mm 14mm" }}>
        {/* brand */}
        <div className="flex items-center gap-[2.5mm]">
          <span style={{ width: "3.2mm", height: "3.2mm", background: TEAL }} />
          <span className="font-display font-bold tracking-tight" style={{ fontSize: "17pt" }}>
            Sebyx
          </span>
        </div>
        <div
          className="font-mono uppercase mt-[1.5mm]"
          style={{ fontSize: "6.5pt", letterSpacing: "0.45em", color: "#8A94A6" }}
        >
          Revenue Architecture · Sebyx Academy
        </div>

        <div
          className="font-mono uppercase mt-[9mm]"
          style={{ fontSize: "9.5pt", letterSpacing: "0.55em", color: TEAL }}
        >
          Certificate of Completion
        </div>

        <div className="mt-[8mm]" style={{ fontSize: "10.5pt", color: "#8A94A6" }}>
          This certifies that
        </div>

        {/* name */}
        <div className="mt-[3mm] w-full flex flex-col items-center">
          {name ? (
            <div
              className="font-display font-semibold italic tracking-tight"
              style={{ fontSize: "34pt", lineHeight: 1.05 }}
            >
              {name}
            </div>
          ) : (
            <div style={{ width: "150mm", borderBottom: `0.4mm solid ${TEAL}`, height: "14mm" }} />
          )}
          <div className="mt-[2.5mm]" style={{ width: "62mm", height: "0.5mm", background: TEAL }} />
        </div>

        <div className="mt-[6mm]" style={{ fontSize: "10.5pt", color: "#8A94A6" }}>
          has successfully completed the invitation-only programme
        </div>

        <div className="font-display font-bold tracking-tight mt-[2.5mm]" style={{ fontSize: "24pt" }}>
          {cfg.courseName}
        </div>
        <div className="mt-[2.5mm] max-w-[190mm]" style={{ fontSize: "9.5pt", color: "#8A94A6", lineHeight: 1.5 }}>
          {cfg.descriptor}
        </div>

        {/* bottom row */}
        <div className="mt-auto w-full grid grid-cols-3 items-end gap-[10mm]">
          <div className="text-center">
            {date ? (
              <div className="font-display" style={{ fontSize: "11.5pt", paddingBottom: "1.5mm" }}>{date}</div>
            ) : (
              <div style={{ borderBottom: `0.3mm solid #475062`, height: "9mm" }} />
            )}
            <div
              className="font-mono uppercase mt-[1.5mm] pt-[1.5mm]"
              style={{ fontSize: "6.5pt", letterSpacing: "0.3em", color: "#8A94A6", borderTop: date ? `0.3mm solid #475062` : "none" }}
            >
              Date{city ? ` · ${city}` : " · Cohort City"}
            </div>
          </div>

          <div className="flex justify-center pb-[1mm]">
            <Seal />
          </div>

          <div className="text-center">
            <div
              className="font-display italic"
              style={{ fontSize: "13pt", color: TEAL, height: "9mm", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
            >
              Mandar Prabhudesai
            </div>
            <div
              className="font-mono uppercase mt-[1.5mm] pt-[1.5mm]"
              style={{ fontSize: "6.5pt", letterSpacing: "0.3em", color: "#8A94A6", borderTop: `0.3mm solid #475062` }}
            >
              Founder & Strategic Architect
            </div>
          </div>
        </div>

        <div
          className="font-mono uppercase mt-[5mm]"
          style={{ fontSize: "5.5pt", letterSpacing: "0.3em", color: "#475062" }}
        >
          {cfg.certPrefix}-2026-________ · 12 hours · 8 modules · final live drill passed · sebyx.com
        </div>
      </div>
    </div>
  );
}

function CertificatePage({ cfg }) {
  const [params] = useSearchParams();
  const [name, setName] = useState(params.get("name") || "");
  const [date, setDate] = useState(params.get("date") || "");
  const [city, setCity] = useState(params.get("city") || "");

  return (
    <main className="min-h-screen font-body" style={{ background: "#3a4150" }}>
      <style>{`@media print { @page { size: A4 landscape; margin: 0; } }`}</style>
      <header className="print:hidden sticky top-0 z-50 bg-sebyx-bg/95 backdrop-blur border-b border-sebyx-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group text-sebyx-text shrink-0">
            <ArrowLeft size={16} className="text-sebyx-accent group-hover:-translate-x-0.5 transition-transform" />
            <span className="block w-2 h-2 bg-sebyx-accent" />
            <span className="font-display text-xl font-semibold tracking-tight">Sebyx</span>
          </Link>
          <div className="flex items-center gap-3 flex-1 justify-end">
            <input
              data-testid="cert-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Participant name"
              className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-3 py-2 text-sm w-52 focus:border-sebyx-accent outline-none"
            />
            <input
              data-testid="cert-date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="Date (e.g., 14 June 2026)"
              className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-3 py-2 text-sm w-44 focus:border-sebyx-accent outline-none"
            />
            <input
              data-testid="cert-city-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="bg-sebyx-bg border border-sebyx-border text-sebyx-text px-3 py-2 text-sm w-32 focus:border-sebyx-accent outline-none"
            />
            <button
              data-testid="cert-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sebyx-accent text-sebyx-bg text-sm font-semibold hover:bg-[#3FA097] transition-colors shrink-0"
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>
      </header>
      <div className="py-6 print:py-0 overflow-x-auto">
        <CertificateSheet cfg={cfg} name={name} date={date} city={city} />
      </div>
    </main>
  );
}

const FRONTLINE_CERT = {
  slug: "frontline-elite",
  courseName: "Frontline Elite — Premium Retail Sales",
  descriptor:
    "A 12-hour intensive sprint across 8 modules — premium customer psychology, consultative discovery, objection handling, and the clean close — completed with 60+ field scripts and the final live drill.",
  certPrefix: "SFE",
};

const ROOKIE_CERT = {
  slug: "rookie-hunter",
  courseName: "Rookie Hunter — B2B Sales & Pipeline",
  descriptor:
    "A 12-hour intensive sprint across 8 modules — cold outreach, discovery, objection handling, multi-threading and the 7-touch cadence — completed with 60+ scripts and the final live drill.",
  certPrefix: "SRH",
};

export function FrontlineCertificate() {
  return (
    <>
      <SEO
        title="Frontline Elite Certificate | Sebyx Academy"
        description="Certificate of completion — Sebyx Frontline Elite premium retail sales programme."
        path="/frontline-certificate"
      />
      <CertificatePage cfg={FRONTLINE_CERT} />
    </>
  );
}

export function RookieCertificate() {
  return (
    <>
      <SEO
        title="Rookie Hunter Certificate | Sebyx Academy"
        description="Certificate of completion — Sebyx Rookie Hunter B2B sales programme."
        path="/rookie-certificate"
      />
      <CertificatePage cfg={ROOKIE_CERT} />
    </>
  );
}
